import { createPortal } from 'react-dom';
import css from './index.module.scss';
import Question from './../A/icons/Question';
import QuestionHover from './../A/icons/QuestionHover';
import DetailModal from './DetailModal';
import QuestionModal from './../A/QuestionModal';
import { useGlobal } from '@/utils/useGlobal';

export default function Step2B({ calcObj, callback }) {
    const { useLang, useRouter, useState, useEffect, gtm } = useGlobal();
    const { locale } = useRouter();
    const { t } = useLang('dpu_calculator');
    const [isClient, setIsClient] = useState(false);
    const [showDetail, setShowDetail] = useState(null);
    const [check, setCheck] = useState(calcObj?.checkB || 1);
    const [peakCharges, setPeakCharges] = useState(calcObj?.peakCharges ? Math.round(100 * calcObj?.peakCharges) / 100 : '');
    const [offpeakCharges, setOffPeakCharges] = useState(calcObj?.offpeakCharges ? Math.round(100 * calcObj?.offpeakCharges) / 100 : '');
    const [bill, setBill] = useState(calcObj?.bill ? Math.round(100 * calcObj?.bill) / 100 : '');
    const [energy, setEnergy] = useState(calcObj?.totalPower ? Math.round(100 * (calcObj?.totalPower / 1000) * 30) / 100 : '');
    const chargesRegex = /^\d*?(\d|\.|\.\d{1,2})$/g;
    const [question, setQuestion] = useState(null);

    const selections = [
        {
            id: 'basic',
            title: t('step2_B_select1_title'),
            subtitle: t('step2_B_select1_subtitle'),
            power: t('step2_B_select1_power'),
            desc: t('step2_B_select1_desc'),
            img: '/responsive/picture/dpu-calculator/2_1.jpg',
        },
        {
            id: 'advanced',
            title: t('step2_B_select2_title'),
            subtitle: t('step2_B_select2_subtitle'),
            power: t('step2_B_select2_power'),
            desc: t('step2_B_select2_desc'),
            img: '/responsive/picture/dpu-calculator/2_2.jpg',
        },
        {
            id: 'hassle',
            title: t('step2_B_select3_title'),
            subtitle: t('step2_B_select3_subtitle'),
            power: t('step2_B_select3_power'),
            desc: t('step2_B_select3_desc'),
            img: '/responsive/picture/dpu-calculator/2_3.jpg',
        },
    ];

    const handleOpenQuestion = () => {
        setQuestion({
            text: t.rich('step2_B_plan_text', {
                b: (txt: string) => <span className="b">{txt}</span>,
                r: (txt: string) => <span className="r">{txt}</span>,
            }),
        });
    };

    const handleBillChange = (e: any) => {
        const v = e.target.value;
        if ((!chargesRegex.test(v) || v.length > 5) && v) {
            return;
        }
        setBill(v);
        callback({ ...calcObj, bill: Number(v) });
    };

    const handleEnergyChange = (e: any) => {
        const v = e.target.value;
        if ((!chargesRegex.test(v) || v.length > 5) && v) {
            return;
        }
        setEnergy(v);
        const value = (Number(v) ?? 0) / 30 / 2 / (6.144 * 0.9);
        const checkB = value < 5 ? 1 : value < 10 ? 2 : 3;
        setCheck(checkB);
        callback({ ...calcObj, checkB, totalPower: (Number(v) * 1000) / 30, energy: Number(v) });
    };

    const handlePeakChange = (e: any) => {
        const v = e.target.value;
        if ((!chargesRegex.test(v) || v.length > 5 || Number(v) > 99.99) && v) {
            return;
        }
        setPeakCharges(v);
        callback({ ...calcObj, peakCharges: Number(v) });
    };

    const handleOffPeakChange = (e: any) => {
        const v = e.target.value;
        if ((!chargesRegex.test(v) || v.length > 5 || Number(v) > 99.99) && v) {
            return;
        }
        setOffPeakCharges(v);
        callback({ ...calcObj, offpeakCharges: Number(v) });
    };

    // const handleChangeCheck = (e: any, k: number) => {
    //     if (e.target.checked) {
    //         setCheck(k + 1);
    //         callback({ ...calcObj, checkB: k + 1 });
    //     }
    // };

    useEffect(() => {
        if (typeof window !== 'undefined') {
            setIsClient(true);
        }
    }, []);

    return (
        <div className={`dpu-calculator-step2-B ${css['dpu-calculator-step2-B']}`}>
            <div className="plan-box bill-box">
                <div className="plan-title-box">
                    <span className="plan-title">{t('step2_B_bill')}</span>
                </div>
                <div className="inner-box">
                    <div className="item">
                        <p className="item-label">{t('step2_B_bill_electric')}</p>
                        <div className="input-box">
                            <span className="currency">{t('step2_B_currency')}</span>
                            <input type="text" className="input" maxLength={5} value={bill} onChange={handleBillChange} />
                            <span className="currency">{t('step2_B_mon')}</span>
                        </div>
                    </div>
                    <div className="item">
                        <p className="item-label">{t('step2_B_bill_consumption')}</p>
                        <div className="input-box">
                            <input type="text" className="input" maxLength={5} value={energy} onChange={handleEnergyChange} />
                            <span className="currency">{t('step2_B_kwh_mon')}</span>
                        </div>
                    </div>
                </div>
            </div>
            <div className="plan-box">
                <div className="plan-title-box">
                    <span className="plan-title">{t('step2_B_plan')}</span>
                    <span className="question-icon" onClick={handleOpenQuestion}>
                        <Question />
                        <QuestionHover />
                    </span>
                </div>
                <div className="inner-box">
                    <div className="item">
                        <p className="item-label">{t('step2_B_plan_peak')}</p>
                        <div className="input-box">
                            <span className="currency">{t('step2_B_currency')}</span>
                            <input type="text" className="input" maxLength={5} value={peakCharges} onChange={handlePeakChange} />
                            <span className="currency">/{t('step3_kwh')}</span>
                        </div>
                    </div>
                    <div className="item">
                        <p className="item-label">{t('step2_B_plan_offpeak')}</p>
                        <div className="input-box">
                            <span className="currency">{t('step2_B_currency')}</span>
                            <input type="text" className="input" maxLength={5} value={offpeakCharges} onChange={handleOffPeakChange} />
                            <span className="currency">/{t('step3_kwh')}</span>
                        </div>
                    </div>
                </div>
            </div>
            <div className="selection-box">
                <p className="selection-title">{t('step2_B_select')}</p>
                <ul className="selections">
                    {selections?.map((selection: any, k: number) => (
                        <li className={`selection ${check === k + 1 ? 'active' : ''}`} key={k}>
                            <div className="select-info">
                                <p className="select-title">{selection.title}</p>
                                <p className="select-power">{selection.power}</p>
                                <p className="select-desc">{selection.desc}</p>
                            </div>
                            <div className="select-bottom" onClick={() => setShowDetail(selection)}>
                                {t('step2_B_detail')} &gt;
                            </div>
                            {/* <div className="check-box">
                                <Checkbox checked={check === k + 1} onChange={(e: any) => handleChangeCheck(e, k)} />
                            </div> */}
                        </li>
                    ))}
                </ul>
            </div>
            {isClient && showDetail && createPortal(<DetailModal selection={showDetail} callback={() => setShowDetail(null)} />, document.body)}
            {question && isClient && createPortal(<QuestionModal app={question} callback={() => setQuestion(null)} />, document.body)}
        </div>
    );
}
